import React, { useEffect, useState, useCallback } from "react";
import { Truck, FileText, PackageCheck, MapPin, CheckCircle2, Loader2, Check, AlertTriangle } from "lucide-react";
import empApi from "../../lib/empApi";

const STAGE = {
  READY: { label: "Siap Dimuat", tone: "bg-amber-500/20 text-amber-300 border-amber-500/40", next: "LOADED", action: "Tandai Dimuat" },
  LOADED: { label: "Dimuat", tone: "bg-blue-500/20 text-blue-300 border-blue-500/40", next: "IN_TRANSIT", action: "Berangkat" },
  IN_TRANSIT: { label: "Dalam Perjalanan", tone: "bg-violet-500/20 text-violet-300 border-violet-500/40", next: "DELIVERED", action: "Tandai Terkirim" },
  DELIVERED: { label: "Terkirim", tone: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", next: null, action: null },
};

export default function EmpDeliveries() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [checked, setChecked] = useState({}); // loading checklist per order

  const load = useCallback(async () => {
    try { const { data } = await empApi.get("/employee/driver/deliveries"); setData(data); }
    catch (e) { setMsg(e.response?.data?.detail || "Gagal memuat pengiriman"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const advance = async (d) => {
    const stage = STAGE[d.stage]?.next;
    if (!stage) return;
    setBusy(d.order_id); setMsg(null);
    try {
      await empApi.post(`/employee/driver/deliveries/${d.order_id}/status`, { stage });
      await load();
    } catch (e) { setMsg(e.response?.data?.detail || "Gagal memperbarui status"); }
    finally { setBusy(null); }
  };

  const openNote = async (d) => {
    try {
      const res = await empApi.get(`/employee/driver/deliveries/${d.order_id}/surat-jalan`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch (e) { setMsg("Gagal membuka Surat Jalan"); }
  };

  if (!data) return (
    <div className="py-16 text-center text-slate-400">
      {msg ? <div className="inline-flex items-center gap-2 rounded-xl bg-red-500/15 p-3 text-sm text-red-300"><AlertTriangle size={16} /> {msg}</div>
           : <Loader2 className="mx-auto animate-spin" />}
    </div>
  );

  return (
    <div data-testid="emp-deliveries" className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-400/20 text-amber-300"><Truck size={22} /></span>
        <div>
          <h1 className="text-lg font-bold text-white">Pengiriman Hari Ini</h1>
          <p className="text-xs text-slate-400">{data.summary.active} aktif · {data.summary.delivered_today} terkirim</p>
        </div>
      </div>

      {msg && <div className="flex items-center gap-2 rounded-xl bg-red-500/15 p-3 text-sm text-red-300"><AlertTriangle size={16} /> {msg}</div>}

      {data.deliveries.length === 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-center text-sm text-slate-400" data-testid="emp-deliveries-empty">
          Belum ada pengiriman untuk Anda hari ini.
        </div>
      )}

      {data.deliveries.map((d) => {
        const st = STAGE[d.stage] || STAGE.READY;
        const items = d.items || [];
        const allChecked = items.every((_, i) => checked[`${d.order_id}-${i}`]);
        const needsChecklist = d.stage === "READY";
        return (
          <div key={d.order_id} className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4" data-testid={`emp-delivery-${d.order_no}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-bold text-white">{d.order_no}</p>
                <p className="flex items-center gap-1 text-xs text-slate-400"><MapPin size={12} /> {d.branch_name} · {d.item_count} item</p>
              </div>
              <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${st.tone}`} data-testid={`emp-delivery-stage-${d.order_no}`}>{st.label}</span>
            </div>

            {/* Loading checklist (delivery note content) */}
            <div className="mt-3 rounded-xl bg-slate-950/60 p-3">
              <p className="mb-2 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-500"><PackageCheck size={13} /> Checklist Muatan</p>
              <div className="space-y-1.5">
                {items.map((it, i) => {
                  const key = `${d.order_id}-${i}`;
                  const on = !!checked[key];
                  return (
                    <button key={key} disabled={!needsChecklist} onClick={() => setChecked((c) => ({ ...c, [key]: !on }))}
                      data-testid={`emp-check-${d.order_no}-${i}`}
                      className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm ${on ? "bg-emerald-500/15" : "bg-slate-800/50"} ${needsChecklist ? "" : "opacity-70"}`}>
                      <span className={`flex h-5 w-5 items-center justify-center rounded ${on ? "bg-emerald-500 text-white" : "border border-slate-600"}`}>{on && <Check size={13} />}</span>
                      <span className="flex-1 text-slate-200">{it.product_name}</span>
                      <span className="text-slate-400">{it.qty} {it.unit}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-3 flex gap-2">
              <button onClick={() => openNote(d)} data-testid={`emp-sj-${d.order_no}`}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-700 py-2.5 text-sm font-bold text-slate-200 transition hover:bg-slate-800">
                <FileText size={15} /> Surat Jalan
              </button>
              {st.action && (
                <button onClick={() => advance(d)} disabled={busy === d.order_id || (needsChecklist && items.length > 0 && !allChecked)}
                  data-testid={`emp-advance-${d.order_no}`}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 py-2.5 text-sm font-bold text-slate-900 transition disabled:opacity-40">
                  {busy === d.order_id ? <Loader2 size={15} className="animate-spin" /> : <Truck size={15} />} {st.action}
                </button>
              )}
              {!st.action && <span className="flex flex-1 items-center justify-center gap-1.5 text-sm font-bold text-emerald-300"><CheckCircle2 size={15} /> Selesai</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
